import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthCard, Input, Button } from '../../components';
import api from '../../utils/api';
import './ResetPassword.scss';

/**
 * Password-reset submission page (#507).
 *
 * Reached via the email link `${CLIENT_URL}/reset-password?token=xyz`.
 * Renders a new-password form; on submit posts to
 * `/api/auth/reset-password` with the token from the query string.
 *
 * Mirrors ForgotPassword.tsx's shape: same AuthCard, same success-
 * message pattern. No auto-login on success — user is bounced to
 * /login so they explicitly sign in with the new credential.
 */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Missing/blank token means the user navigated here directly rather
  // than via an email link. Show a helpful message rather than a
  // generic server 400 after submit.
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    if (!token) setTokenMissing(true);
  }, [token]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!newPassword || !confirmPassword) {
      setError('Please fill in both password fields');
      return;
    }
    if (newPassword.length < 8) {
      // Matches the server-side minimum in ResetPasswordSchema.
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword });
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to reset password. The link may have expired — request a new one.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenMissing) {
    return (
      <AuthCard
        title="Invalid Reset Link"
        subtitle="This page is reached via an email link"
      >
        <p className="reset-hint">
          The password-reset link seems to be missing its token. Try clicking the
          link in your email again, or request a fresh reset below.
        </p>
        <Link to="/forgot-password" className="back-to-login">
          <Button variant="primary" fullWidth>
            Request Password Reset
          </Button>
        </Link>
      </AuthCard>
    );
  }

  if (success) {
    return (
      <AuthCard
        title="Password Reset"
        subtitle="You can now log in with your new password"
      >
        <div className="success-message">
          <p>Your password has been updated successfully.</p>
        </div>
        <Link to="/login" className="back-to-login">
          <Button variant="primary" fullWidth>
            Go to Login
          </Button>
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Reset Your Password"
      subtitle="Choose a new password for your account"
      footerText="Remember your password?"
      footerLink={{ text: 'Log in', to: '/login' }}
    >
      <form onSubmit={handleSubmit} className="reset-password-form">
        {error && <div className="error-banner">{error}</div>}

        <Input
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
          disabled={isLoading}
          autoComplete="new-password"
        />
        <Input
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Re-enter your new password"
          required
          disabled={isLoading}
          autoComplete="new-password"
        />

        <Button
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          isLoading={isLoading}
          disabled={!newPassword || !confirmPassword}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </form>
    </AuthCard>
  );
};

export default ResetPassword;
